import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

/**
 * Hook to check if the current user is a super admin.
 * Uses SWR with localStorage provider to prevent layout shifts.
 */
export const useSuperAdminCheck = () => {
  // Only use fallback if we have a cached value
  const cachedValue = typeof window !== 'undefined' ? localStorage.getItem('is_superadmin') : null;
  const cachedPromptValue = typeof window !== 'undefined' ? localStorage.getItem('is_prompt_superadmin') : null;

  const { data, isLoading } = useSWR('/admin/check-access', fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 86400000, // 24 hours
    revalidateOnMount: true,
    // Only provide fallback data if we have a cached value, otherwise let SWR fetch
    ...((cachedValue !== null && cachedPromptValue !== null) && {
      fallbackData: { is_super_admin: cachedValue === 'true', is_prompt_super_admin: cachedPromptValue === 'true' }
    }),
    onSuccess: (data) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('is_superadmin', String(data?.is_super_admin || false));
        localStorage.setItem('is_prompt_superadmin', String(data?.is_prompt_super_admin || false));
      }
    },
  });

  // Use cached value immediately if data hasn't loaded yet to prevent flicker
  const isSuperAdmin = data?.is_super_admin ?? (cachedValue === 'true');
  const isPromptSuperAdmin = data?.is_prompt_super_admin ?? (cachedPromptValue === 'true');

  return {
    isSuperAdmin,
    isPromptSuperAdmin,
    isLoading,
  };
};
